// Lead Generation System — staff Prelead monitor. Managers watch every
// generator's production line here; the generator drives their own pipeline
// from the portal console, so this surface is read-only. Mirrors the Leads
// page (stat cards → filters → table → pagination) and adds an Escalated
// column that links to the resulting record in the Leads pipeline.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Eye } from "lucide-react";
import { Badge, Button, Input } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { FilterChipGroup } from "@/components/portfolioAdmin/registry/FilterChipGroup";
import { PageHeader } from "@/components/layout/PageHeader";
import { Pagination } from "@/components/layout/Pagination";
import { useDateFormatter } from "@/utils/datetime";
import {
  calendarDateInputToIso,
  displayEmail,
  formatCurrency,
  formatPayWeekRange,
  formatPhone,
} from "@/design-system/lib/format";
import { US_STATES } from "@/lib/usStates";
import type { PreleadListQuery, PreleadSource, PreleadStatus, PreleadSummaryDto } from "@/api/leadGen/preleads";
import { usePreleads, usePreleadStats, useWindowedPreleadStats } from "@/hooks/leadGen/usePreleads";
import { useLeadGenerators } from "@/hooks/leadGen/useLeadGenerators";
import { useAuth } from "@/hooks/useAuth";
import {
  INTEREST_BADGE,
  PRELEAD_SOURCE_LABEL,
  PRELEAD_SOURCES,
  PRELEAD_STATUS_BADGE,
  PRELEAD_STATUS_LABEL,
  PRELEAD_STATUSES,
  preleadDisplayName,
} from "./leadGenShared";
import { ConfirmedBadges, PreleadDetailDrawer } from "./PreleadDetailDrawer";
import { PreleadStaffActions } from "./PreleadStaffActions";

const PAGE_SIZE = 25;
const MONEY = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;
const SELECT_CLASS =
  "h-10 w-44 rounded-md border border-border-light bg-white-pure px-space-2 text-small";

// Multi-select milestone filter (AND semantics) — a prelead matches only when it
// has reached EVERY selected token. Sent as repeated `?statuses=` params.
const MILESTONE_FILTER_OPTIONS = [
  { value: "message_sent", label: "Message sent" },
  { value: "response_received", label: "Response received" },
  { value: "nda_complete", label: "NDA complete" },
  { value: "cim_received", label: "CIM received" },
  { value: "interested", label: "Interested" },
  { value: "escalated", label: "Escalated" },
  { value: "confirmed_contacted", label: "Confirmed contacted" },
  { value: "confirmed_interested", label: "Confirmed interested" },
] as const;

export function PreleadsListPage() {
  const { date, tz } = useDateFormatter();
  const { hasRoleCode } = useAuth();
  const isAdmin = hasRoleCode("ADMIN");

  const [search, setSearch] = useState("");
  // "Archived" + "Deleted" are sentinels that drive the archived / soft-deleted
  // views; every other value is a real PreleadStatus (mirrors the Leads dropdown).
  const [status, setStatus] = useState<PreleadStatus | "" | "Archived" | "Deleted">("");
  // Multi-select milestone filter (AND semantics) — orthogonal to the single
  // headline `status` dropdown; both are AND-combined server-side.
  const [milestones, setMilestones] = useState<string[]>([]);
  const [source, setSource] = useState<PreleadSource | "">("");
  const [state, setState] = useState("");
  const [generatorId, setGeneratorId] = useState("");
  const [dateField, setDateField] = useState<"created" | "updated">("created");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const archivedView = status === "Archived";
  const deletedView = status === "Deleted";
  const statusParam: PreleadStatus | undefined =
    status === "" || status === "Archived" || status === "Deleted" ? undefined : status;

  useEffect(() => {
    setPage(1);
  }, [search, status, milestones, source, state, generatorId, dateField, fromDate, toDate]);

  const listParams: PreleadListQuery = {
    page,
    pageSize: PAGE_SIZE,
    q: search || undefined,
    status: statusParam,
    statuses: milestones.length > 0 ? milestones : undefined,
    source: source || undefined,
    state: state || undefined,
    leadGeneratorId: generatorId || undefined,
    archived: archivedView || undefined,
    deleted: deletedView || undefined,
    dateField,
    // The date picker emits bare YYYY-MM-DD (midnight); widen the bounds so a
    // same-day record isn't excluded by the inclusive predicate.
    from: fromDate ? `${fromDate}T00:00:00.000Z` : undefined,
    to: toDate ? `${toDate}T23:59:59.999Z` : undefined,
  };
  // Stats intentionally omit status + archived (the server excludes both so the
  // per-status breakdown stays meaningful when a caller drills into one status).
  const statsParams: PreleadListQuery = {
    q: search || undefined,
    source: source || undefined,
    state: state || undefined,
    leadGeneratorId: generatorId || undefined,
  };

  const list = usePreleads(listParams);
  const statsQuery = usePreleadStats(statsParams);
  const byStatus = statsQuery.data?.byStatus;

  // "This pay week" counts that reconcile with the Earnings Review. The window snaps
  // (server-side) to the pay week containing the filter's start date, or the current
  // week when no start date is set. Generator-scoped like the per-generator Earnings
  // screen. These IGNORE the Created/Updated dateField — they always use pay-week /
  // approval semantics so the earning-bearing numbers tie out to Earnings.
  const windowAnchor = fromDate ? calendarDateInputToIso(fromDate) ?? undefined : undefined;
  const windowedStats = useWindowedPreleadStats(generatorId || undefined, windowAnchor);
  const w = windowedStats.data;
  const payWeek = w ? formatPayWeekRange(w.weekOf) : null;

  const generators = useLeadGenerators({ page: 1, pageSize: 200 });
  const generatorName = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of generators.data?.data ?? []) m.set(g.id, g.displayName);
    return m;
  }, [generators.data]);

  const totalPages = useMemo(() => {
    const data = list.data;
    if (!data || data.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [list.data]);

  const columns: Column<PreleadSummaryDto>[] = useMemo(
    () => [
      {
        key: "business",
        header: "Business / contact",
        render: (p) => {
          const contact = [p.contactFirstName, p.contactLastName].filter(Boolean).join(" ");
          return (
            <button
              type="button"
              onClick={() => setOpenId(p.id)}
              className="text-left font-semibold text-onyx hover:text-gold-shadow"
            >
              {preleadDisplayName({
                businessName: p.businessName,
                contactFirstName: p.contactFirstName,
                contactLastName: p.contactLastName,
                rawText: p.rawTextSnippet,
                listingUrl: p.listingUrl,
                createdAt: p.createdAt,
              }, tz)}
              {p.businessName && contact && (
                <div className="text-caption text-gray-mid font-normal">{contact}</div>
              )}
            </button>
          );
        },
      },
      {
        key: "contact",
        header: "Phone / Email",
        mobileHidden: true,
        render: (p) => (
          <div className="text-small">
            <div className="font-mono text-caption">{formatPhone(p.phone) || "—"}</div>
            <div className="text-gray-mid text-caption">{displayEmail(p.email) ?? ""}</div>
          </div>
        ),
      },
      {
        key: "location",
        header: "Location",
        mobileHidden: true,
        render: (p) => <span className="text-caption">{[p.city, p.state].filter(Boolean).join(", ") || "—"}</span>,
      },
      {
        key: "generator",
        header: "Generator",
        mobileHidden: true,
        render: (p) => (
          <span className="text-caption text-gray-mid">
            {generatorName.get(p.leadGeneratorId) ?? p.leadGeneratorId.slice(0, 8)}
          </span>
        ),
      },
      {
        key: "source",
        header: "Source",
        mobileHidden: true,
        render: (p) => <span className="text-caption">{PRELEAD_SOURCE_LABEL[p.source]}</span>,
      },
      {
        key: "status",
        header: "Status",
        render: (p) => (
          <div className="flex flex-col items-start gap-space-1">
            <Badge status={PRELEAD_STATUS_BADGE[p.status]}>{PRELEAD_STATUS_LABEL[p.status]}</Badge>
            <ConfirmedBadges
              confirmedContactedAtUtc={p.confirmedContactedAtUtc}
              confirmedInterestedAtUtc={p.confirmedInterestedAtUtc}
            />
          </div>
        ),
      },
      {
        key: "interest",
        header: "Interest",
        mobileHidden: true,
        render: (p) => <Badge status={INTEREST_BADGE[p.interestLevel]}>{p.interestLevel}</Badge>,
      },
      {
        key: "earned",
        header: "Earned",
        mobileHidden: true,
        render: (p) => (
          <span className="text-caption font-semibold tabular-nums text-status-success">
            {formatCurrency(p.totalEarned, MONEY)}
          </span>
        ),
      },
      {
        key: "escalated",
        header: "Escalated",
        render: (p) =>
          p.escalatedToLeadId ? (
            <Link
              to={`/leads/${p.escalatedToLeadId}`}
              className="inline-flex items-center gap-space-1 text-label font-display font-semibold uppercase tracking-label text-gold-shadow hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              View lead <ArrowUpRight size={13} strokeWidth={1.5} aria-hidden />
            </Link>
          ) : (
            <span className="text-caption text-gray-mid">—</span>
          ),
      },
      {
        key: "created",
        header: "Created",
        mobileHidden: true,
        render: (p) => <span className="text-caption">{date(p.createdAt)}</span>,
      },
      {
        key: "actions",
        header: "",
        className: "w-20 text-right",
        render: (p) => (
          <div className="flex items-center justify-end gap-space-1">
            {!deletedView && (
              <Button variant="tertiary" size="sm" onClick={() => setOpenId(p.id)} aria-label="Open prelead">
                <Eye size={14} strokeWidth={1.5} aria-hidden />
              </Button>
            )}
            <PreleadStaffActions
              prelead={{
                id: p.id,
                status: p.status,
                isArchived: p.isArchived,
                displayName: preleadDisplayName({
                  businessName: p.businessName,
                  contactFirstName: p.contactFirstName,
                  contactLastName: p.contactLastName,
                  rawText: p.rawTextSnippet,
                  listingUrl: p.listingUrl,
                  createdAt: p.createdAt,
                }, tz),
              }}
              deletedView={deletedView}
            />
          </div>
        ),
      },
    ],
    [date, tz, generatorName, deletedView],
  );

  return (
    <>
      <PageHeader
        eyebrow="Lead Generation"
        title="Preleads"
        description="The generators' production line. Phoenix is the Loan Administrator — preleads are business-for-sale contacts a generator sourced, escalated into the Leads pipeline when they're interested. This monitor is read-only."
      />

      {payWeek && (
        <div className="text-caption text-gray-mid mb-space-2">
          <span className="font-semibold text-onyx">This pay week: {payWeek.rangeLabel}</span>
          {" — the “this pay week” numbers reconcile with the Earnings Review; the big number is all-time."}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-space-3 mb-space-4">
        <StatCard label="Total" value={statsQuery.data?.total ?? 0} />
        <StatCard label="Message sent" value={byStatus?.MessageSent ?? 0} windowValue={w?.messageSent} />
        <StatCard label="NDA / CIM" value={w?.ndaCimAllTime ?? 0} windowValue={w?.ndaCim} />
        <StatCard label="Appointment" value={w?.appointmentAllTime ?? 0} windowValue={w?.appointment} />
        <StatCard label="Escalated" value={byStatus?.Escalated ?? 0} windowValue={w?.escalated} />
        <StatCard label="Response" value={byStatus?.ResponseReceived ?? 0} windowValue={w?.response} windowNote="activity only" />
        <StatCard label="Interested" value={byStatus?.Interested ?? 0} windowValue={w?.interested} windowNote="activity only" />
        <StatCard label="Contacted" value={byStatus?.Contacted ?? 0} windowValue={w?.contacted} windowNote="activity only" />
      </div>

      <div className="flex flex-col gap-space-3 mb-space-3">
        <Input
          type="search"
          placeholder="Search business, contact, phone, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-space-3">
          <select
            className={SELECT_CLASS}
            value={status}
            onChange={(e) => setStatus(e.target.value as PreleadStatus | "" | "Archived" | "Deleted")}
            aria-label="Status filter"
          >
            <option value="">All statuses</option>
            {PRELEAD_STATUSES.map((s) => (
              <option key={s} value={s}>
                {PRELEAD_STATUS_LABEL[s]}
              </option>
            ))}
            <option value="Archived">Archived</option>
            {isAdmin && <option value="Deleted">Deleted</option>}
          </select>
          <select
            className={SELECT_CLASS}
            value={source}
            onChange={(e) => setSource(e.target.value as PreleadSource | "")}
            aria-label="Source filter"
          >
            <option value="">All sources</option>
            {PRELEAD_SOURCES.map((s) => (
              <option key={s} value={s}>
                {PRELEAD_SOURCE_LABEL[s]}
              </option>
            ))}
          </select>
          <select
            className={SELECT_CLASS}
            value={state}
            onChange={(e) => setState(e.target.value)}
            aria-label="State filter"
          >
            <option value="">All states</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
          <select
            className={SELECT_CLASS}
            value={generatorId}
            onChange={(e) => setGeneratorId(e.target.value)}
            aria-label="Generator filter"
          >
            <option value="">All generators</option>
            {(generators.data?.data ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.displayName}
              </option>
            ))}
          </select>
          <select
            className={SELECT_CLASS}
            value={dateField}
            onChange={(e) => setDateField(e.target.value as "created" | "updated")}
            aria-label="Date field"
          >
            <option value="created">Created</option>
            <option value="updated">Updated</option>
          </select>
          <input
            type="date"
            className={SELECT_CLASS}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            aria-label="From date"
          />
          <input
            type="date"
            className={SELECT_CLASS}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            aria-label="To date"
          />
        </div>
        <FilterChipGroup
          label="Milestones reached (all selected)"
          testId="prelead-milestone-filter"
          options={MILESTONE_FILTER_OPTIONS}
          selected={milestones}
          onChange={setMilestones}
        />
      </div>

      <DataTable
        columns={columns}
        rows={list.data?.data ?? []}
        loading={list.isLoading}
        error={list.isError ? (list.error as Error).message : null}
        emptyMessage="No preleads match your filters."
        rowKey={(p) => p.id}
      />

      {list.data && list.data.totalCount > 0 && (
        <Pagination
          page={list.data.page}
          pageSize={list.data.pageSize}
          totalCount={list.data.totalCount}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}

      {openId && <PreleadDetailDrawer preleadId={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function StatCard({
  label,
  value,
  windowValue,
  windowNote,
}: {
  label: string;
  value: number;
  /** The "this pay week" count. Omit for cards with no windowed number (Total). */
  windowValue?: number;
  /** A qualifier for the windowed number, e.g. "activity only" for non-earning cards. */
  windowNote?: string;
}) {
  return (
    <div className="border border-border-light rounded-md p-space-3 bg-white-pure">
      <div className="text-caption uppercase tracking-wide text-gray-mid">{label}</div>
      <div className="font-display text-h3 text-onyx">
        {value} <span className="text-caption font-normal text-gray-mid">all-time</span>
      </div>
      {windowValue !== undefined && (
        <div className="text-caption font-semibold text-gold-shadow">
          {windowValue} this pay week
          {windowNote ? <span className="font-normal text-gray-mid"> · {windowNote}</span> : null}
        </div>
      )}
    </div>
  );
}
